import { useCallback, useEffect, useRef, useState } from "react";
import { isPackagingWorkOrderPending } from "../../../shared/manual-packaging/packaging-work-order";
import { apiRequest } from "../../shared/api/client";
import { pendingOrderCheckKey, validatePendingOrderCheck, type PendingOrderCheckState } from "./pending-work-order-check";
import type { PackagingPlanRow, Week39PackagingSnapshot } from "./week39-types";

export function usePendingWorkOrderChecks(snapshot: Week39PackagingSnapshot | null) {
  const weekStart = snapshot?.weekStart ?? "";
  const scope = JSON.stringify([weekStart, snapshot?.source.sha256, snapshot?.lines.flatMap(line => line.rows)
    .filter(row => isPackagingWorkOrderPending(row) || row.manualWorkOrderBinding).map(row => [row.sourceRow, row.productCode, row.country, row.manualWorkOrderBinding?.version])]);
  const [state, setState] = useState<{ scope: string; checks: Record<string, PendingOrderCheckState> }>({ scope: "", checks: {} });
  const requests = useRef(new Map<string, AbortController>());
  useEffect(() => {
    const active = requests.current;
    return () => { for (const request of active.values()) request.abort(); active.clear(); };
  }, [scope]);
  const check = useCallback(async (row: PackagingPlanRow, after?: string, workOrderNo?: string) => {
    if (!weekStart || (!isPackagingWorkOrderPending(row) && !row.manualWorkOrderBinding)) return;
    const key = pendingOrderCheckKey(row);
    if (requests.current.has(key)) return;
    const controller = new AbortController();
    requests.current.set(key, controller);
    const update = (change: Partial<PendingOrderCheckState>) => setState(previous => ({ scope, checks: {
      ...(previous.scope === scope ? previous.checks : {}),
      [key]: { loading: false, error: "", data: null, ...(previous.scope === scope ? previous.checks[key] : {}), ...change },
    } }));
    update({ loading: true, error: "" });
    let timedOut = false;
    const timeout = window.setTimeout(() => { timedOut = true; controller.abort(); }, 25_000);
    try {
      const data = validatePendingOrderCheck(await apiRequest<unknown>(
        `/api/sarf39/manual-packaging/pending-work-order?weekStart=${weekStart}&sourceRow=${row.sourceRow}${after ? `&after=${encodeURIComponent(after)}` : ""}${workOrderNo ? `&workOrderNo=${encodeURIComponent(workOrderNo)}` : ""}`,
        { cache: "no-store", signal: controller.signal }), row);
      if (!controller.signal.aborted) update({ loading: false, data });
    } catch (error) {
      if (!controller.signal.aborted || timedOut) update({ loading: false, error: timedOut
        ? "Mikro yanıt vermedi. Yeniden kontrol et." : error instanceof Error ? error.message : "Mikro kontrolü yapılamadı. Yeniden dene." });
    } finally {
      window.clearTimeout(timeout);
      if (requests.current.get(key) === controller) requests.current.delete(key);
    }
  }, [scope, weekStart]);
  return { checks: state.scope === scope ? state.checks : {}, check };
}
